import { motion } from 'framer-motion';
import { EventCard } from '../Events/EventCard';
import { contactInfo, assets } from '../../data/events';
import { MapPin, ArrowRight, Navigation, ChevronDown } from 'lucide-react';
import { openDirections } from '../Events/EventDetailModal';
import { useState } from 'react';

export const Sidebar = ({ currentEvent, onEventSelect, filteredEvents, allEvents }) => {
  const [showScrollHint, setShowScrollHint] = useState(true);
  const eventsToShow = filteredEvents.length > 0 ? filteredEvents : allEvents;
  const allEventsList = eventsToShow;

  const handleScroll = (e) => {
    if (e.target.scrollTop > 10) {
      setShowScrollHint(false);
    }
  };

  return (
    <motion.aside
      data-testid="sidebar"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
      className="hidden md:flex absolute top-4 left-4 bottom-[160px] w-[380px] z-40 flex-col gap-3 overflow-hidden"
    >
      {/* Header Card - Compact */}
      <div className="glass rounded-2xl p-4 shadow-xl">
        <div className="flex items-center gap-3">
          <img 
            src={assets.logo} 
            alt="Logo L'Atelier by JS - Food Truck Suisse Romande"
            className="w-12 h-12 rounded-full object-cover shadow-md"
          />
          <div>
            <h1 className="font-league text-xl uppercase tracking-wide text-black">
              Où nous trouver ?
            </h1>
            <p className="font-dm text-xs text-[#666666]">
              {contactInfo.slogan}
            </p>
          </div>
        </div>
      </div>

      {/* Current/Next Event Highlight - Compact */}
      <div className="rounded-2xl overflow-hidden shadow-xl flex-shrink-0">
        <EventCard event={currentEvent} isHighlight />
      </div>

      {/* Quick action - Directions */}
      {currentEvent && currentEvent.status !== 'past' && (
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => openDirections(currentEvent)}
          className="flex items-center justify-center gap-2 bg-black text-white py-3 rounded-xl font-dm text-sm font-bold shadow-lg hover:bg-gray-900 transition-colors"
          data-testid="sidebar-directions-btn"
        >
          <Navigation className="w-4 h-4" />
          Y aller - {currentEvent.location}
        </motion.button>
      )}

      {/* ALL Events List - Takes remaining space */}
      <div 
        className="glass rounded-2xl p-4 shadow-xl flex-1 overflow-y-auto relative"
        onScroll={handleScroll}
      >
        <h3 className="font-league text-base uppercase tracking-wide text-black mb-3">
          Tous les événements ({allEvents.length})
        </h3>
        
        <div className="space-y-2">
          {allEventsList.map((event, index) => {
            const isPast = event.status === 'past';
            
            return (
              <motion.button
                key={event.id}
                data-testid={`sidebar-event-${event.id}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * index }}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => {
                  const eventIndex = allEvents.findIndex(e => e.id === event.id);
                  if (eventIndex >= 0) onEventSelect(eventIndex);
                }}
                className={`w-full text-left p-3 rounded-xl transition-all duration-200 ${
                  currentEvent?.id === event.id 
                    ? 'bg-[#97c7c3]/20 border-l-4 border-[#97c7c3] shadow-sm' 
                    : isPast
                      ? 'bg-gray-50 border-l-4 border-transparent opacity-60'
                      : 'bg-white hover:bg-gray-50 border-l-4 border-transparent hover:shadow-sm'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className={`font-league text-sm uppercase tracking-wide leading-tight ${
                        isPast ? 'text-gray-400' : 'text-black'
                      }`}>
                        {event.name}
                      </h4>
                      {isPast && (
                        <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded">
                          Passé
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <MapPin className={`w-3 h-3 ${isPast ? 'text-gray-300' : 'text-[#baa25e]'}`} />
                      <span className={`font-dm text-xs ${isPast ? 'text-gray-400' : 'text-[#666666]'}`}>
                        {event.location}
                      </span>
                      <span className={`font-dm text-xs ${isPast ? 'text-gray-400' : 'text-[#666666]'}`}>•</span>
                      <span className={`font-dm text-xs ${isPast ? 'text-gray-400' : 'text-[#666666]'}`}>
                        {event.displayDates}
                      </span>
                    </div>
                  </div>
                  <ArrowRight className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isPast ? 'text-gray-300' : 'text-gray-400'}`} />
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Scroll hint */}
        {showScrollHint && allEventsList.length > 4 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute bottom-2 left-0 right-0 flex justify-center pointer-events-none"
          >
            <div className="flex items-center gap-1 bg-white/90 px-3 py-1 rounded-full shadow-sm">
              <ChevronDown className="w-3 h-3 text-gray-400 animate-bounce" />
              <span className="font-dm text-[10px] text-gray-400">Défiler pour voir plus</span>
            </div>
          </motion.div>
        )}
      </div>
    </motion.aside>
  );
};
